import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  Building2,
  Plus,
  RefreshCw,
  Landmark,
  Calendar,
  TrendingDown,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Edit2,
  Trash2,
  CreditCard,
  Clock,
  DollarSign,
  Archive,
  RotateCcw,
  CheckCircle,
  XCircle,
} from "lucide-react";


// Color scheme for verticals
const VERTICAL_COLORS = {
  ep_holding: "from-blue-500 to-blue-600",
  logistika: "from-emerald-500 to-emerald-600",
  artcom: "from-purple-500 to-purple-600",
  cargo_cfs: "from-orange-500 to-orange-600",
  hm_spedition: "from-pink-500 to-pink-600",
  adria_global: "from-teal-500 to-teal-600",
};

const VERTICAL_BG_COLORS = {
  ep_holding: "bg-blue-50 border-blue-200",
  logistika: "bg-emerald-50 border-emerald-200",
  artcom: "bg-purple-50 border-purple-200",
  cargo_cfs: "bg-orange-50 border-orange-200",
  hm_spedition: "bg-pink-50 border-pink-200",
  adria_global: "bg-teal-50 border-teal-200",
};

export default function LoansPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ verticals: [], loan_types: [] });
  const [dashboard, setDashboard] = useState(null);
  const [expandedVerticals, setExpandedVerticals] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [activeTab, setActiveTab] = useState("by-vertical");
  const [showClosed, setShowClosed] = useState(false);  // Toggle to show closed loans
  
  const isAdmin = user?.role === "admin" || user?.is_admin || user?.is_staff;

  // Form state
  const [formData, setFormData] = useState({
    vertical_id: "",
    loan_name: "",
    bank: "",
    reference_number: "",
    net_value: "",
    monthly_instalment: "",
    payment_day: "",
    start_date: "",
    end_date: "",
    current_balance: "",
    loan_type: "long_term",
    status: "open",
    notes: "",
  });

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/loans/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to fetch config:", error);
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const url = showClosed 
        ? `${API_URL}/loans/dashboard?include_closed=true`
        : `${API_URL}/loans/dashboard`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load loan data");
    } finally {
      setLoading(false);
    }
  }, [token, showClosed]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const toggleVertical = (verticalId) => {
    setExpandedVerticals((prev) => ({
      ...prev,
      [verticalId]: !prev[verticalId],
    }));
  };

  const formatCurrency = (amount, currency = "EUR") => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat("sl-SI", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    // Handle DD.MM.YYYY format
    if (dateStr.includes(".")) return dateStr;
    // Handle ISO format
    try {
      return new Date(dateStr).toLocaleDateString("sl-SI");
    } catch {
      return dateStr;
    }
  };

  const resetForm = () => {
    setFormData({
      vertical_id: "",
      loan_name: "",
      bank: "",
      reference_number: "",
      net_value: "",
      monthly_instalment: "",
      payment_day: "",
      start_date: "",
      end_date: "",
      current_balance: "",
      loan_type: "long_term",
      status: "open",
      notes: "",
    });
    setEditingLoan(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      net_value: parseFloat(formData.net_value) || 0,
      monthly_instalment: parseFloat(formData.monthly_instalment) || 0,
      payment_day: formData.payment_day ? parseInt(formData.payment_day) : null,
      current_balance: parseFloat(formData.current_balance) || 0,
    };

    try {
      if (editingLoan) {
        await axios.put(`${API_URL}/loans/${editingLoan.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Loan updated successfully");
      } else {
        await axios.post(`${API_URL}/loans/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Loan created successfully");
      }
      setModalOpen(false);
      resetForm();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save loan");
    }
  };

  const handleCloseLoan = async (loanId) => {
    if (!window.confirm("Close this loan? It will be moved to the archive.")) return;
    
    try {
      await axios.post(`${API_URL}/loans/${loanId}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Loan closed successfully");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to close loan");
    }
  };

  const handleReopenLoan = async (loanId) => {
    try {
      await axios.post(`${API_URL}/loans/${loanId}/reopen`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Loan reopened successfully");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to reopen loan");
    }
  };

  const handleEdit = (loan) => {
    setFormData({
      vertical_id: loan.vertical_id,
      loan_name: loan.loan_name,
      bank: loan.bank || "",
      reference_number: loan.reference_number || "",
      net_value: loan.net_value.toString(),
      monthly_instalment: loan.monthly_instalment.toString(),
      payment_day: loan.payment_day?.toString() || "",
      start_date: loan.start_date,
      end_date: loan.end_date,
      current_balance: loan.current_balance.toString(),
      loan_type: loan.loan_type || "long_term",
      status: loan.status || "open",
      notes: loan.notes || "",
    });
    setEditingLoan(loan);
    setModalOpen(true);
  };

  const handleDelete = async (loanId) => {
    if (!window.confirm("Permanently delete this loan? This cannot be undone.")) return;
    
    try {
      await axios.delete(`${API_URL}/loans/${loanId}?hard_delete=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Loan deleted permanently");
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete loan");
    }
  };

  const getVerticalName = (verticalId) => {
    const v = config.verticals.find((v) => v.id === verticalId);
    return v?.full_name || v?.name || verticalId;
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3">
                <Landmark className="h-7 w-7" />
                Bank Loan Tracker
              </h1>
              <p className="text-indigo-200 text-sm mt-1">
                {showClosed ? "Showing all loans (including closed)" : "Showing open loans only"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Toggle Closed Loans */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowClosed(!showClosed)}
                className={`${showClosed ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'}`}
              >
                <Archive className="h-4 w-4 mr-2" />
                {showClosed ? "Hide Closed" : "Show Closed"}
              </Button>
              
              {isAdmin && (
                <Dialog open={modalOpen} onOpenChange={(open) => {
                  setModalOpen(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-white text-indigo-700 hover:bg-indigo-50"
                      data-testid="add-loan-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Loan
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingLoan ? "Edit Loan" : "Add New Loan"}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Company (Vertical)</Label>
                          <Select
                            value={formData.vertical_id}
                            onValueChange={(v) => setFormData({ ...formData, vertical_id: v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select company" />
                            </SelectTrigger>
                            <SelectContent>
                              {config.verticals.map((v) => (
                                <SelectItem key={v.id} value={v.id}>
                                  {v.full_name || v.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Loan Type</Label>
                          <Select
                            value={formData.loan_type}
                            onValueChange={(v) => setFormData({ ...formData, loan_type: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="long_term">Dolgoročni kredit</SelectItem>
                              <SelectItem value="short_term">Kratkoročni kredit</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>Loan Name (KREDITI)</Label>
                        <Input
                          value={formData.loan_name}
                          onChange={(e) => setFormData({ ...formData, loan_name: e.target.value })}
                          placeholder="e.g., Dolgoročni kredit ADDIKO, št. kredita 56614367"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Bank</Label>
                          <Input
                            value={formData.bank}
                            onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                            placeholder="e.g., ADDIKO BANK"
                          />
                        </div>
                        <div>
                          <Label>Reference Number</Label>
                          <Input
                            value={formData.reference_number}
                            onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                            placeholder="e.g., 56614367"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Net Value (NV) €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.net_value}
                            onChange={(e) => setFormData({ ...formData, net_value: e.target.value })}
                            placeholder="350000.00"
                            required
                          />
                        </div>
                        <div>
                          <Label>Monthly Instalment €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.monthly_instalment}
                            onChange={(e) => setFormData({ ...formData, monthly_instalment: e.target.value })}
                            placeholder="4861.11"
                            required
                          />
                        </div>
                        <div>
                          <Label>Payment Day (1-31)</Label>
                          <Input
                            type="number"
                            min="1"
                            max="31"
                            value={formData.payment_day}
                            onChange={(e) => setFormData({ ...formData, payment_day: e.target.value })}
                            placeholder="15"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Start Date</Label>
                          <Input
                            value={formData.start_date}
                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                            placeholder="DD.MM.YYYY"
                            required
                          />
                        </div>
                        <div>
                          <Label>End Date</Label>
                          <Input
                            value={formData.end_date}
                            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                            placeholder="DD.MM.YYYY"
                            required
                          />
                        </div>
                        <div>
                          <Label>Current Balance (STANJE) €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.current_balance}
                            onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                            placeholder="17000.00"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Notes</Label>
                        <Input
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Additional notes..."
                        />
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                          {editingLoan ? "Update Loan" : "Create Loan"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={fetchDashboard}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total Loan Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600">
                {formatCurrency(dashboard?.totals?.total_net_value || 0)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Monthly Instalments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {formatCurrency(dashboard?.totals?.total_monthly_instalment || 0)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Current Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">
                {formatCurrency(dashboard?.totals?.total_current_balance || 0)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Active Loans</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-800">
                {dashboard?.totals?.loan_count || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Payments Alert */}
        {dashboard?.upcoming_payments?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-amber-600" />
              <div>
                <h3 className="font-bold text-amber-800">Upcoming Payments</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  {dashboard.upcoming_payments.slice(0, 5).map((p, idx) => (
                    <Badge key={idx} className="bg-amber-100 text-amber-800 border border-amber-300">
                      {p.loan_name?.substring(0, 30)}... - {formatCurrency(p.amount)} (Day {p.payment_day})
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="by-vertical" className="gap-2" data-testid="tab-by-vertical">
              <Building2 className="h-4 w-4" />
              By Company
            </TabsTrigger>
            <TabsTrigger value="all-loans" className="gap-2" data-testid="tab-all-loans">
              <CreditCard className="h-4 w-4" />
              All Loans
            </TabsTrigger>
          </TabsList>

          {/* By Vertical Tab */}
          <TabsContent value="by-vertical">
            <div className="space-y-4">
              {config.verticals.map((vertical) => {
                const verticalData = dashboard?.by_vertical?.[vertical.id];
                const isExpanded = expandedVerticals[vertical.id];
                const hasLoans = verticalData?.loans?.length > 0;

                return (
                  <Card
                    key={vertical.id}
                    className={`overflow-hidden ${VERTICAL_BG_COLORS[vertical.id] || "bg-slate-50 border-slate-200"}`}
                  >
                    {/* Vertical Header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-white/50 transition-colors"
                      onClick={() => toggleVertical(vertical.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-slate-500" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-slate-500" />
                          )}
                          <Building2 className="h-5 w-5 text-slate-600" />
                          <div>
                            <h3 className="font-bold text-lg">{vertical.full_name || vertical.name}</h3>
                            {hasLoans && (
                              <p className="text-sm text-slate-600">
                                {verticalData.totals.count} loans • Monthly: {formatCurrency(verticalData.totals.monthly_instalment)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {hasLoans ? (
                            <>
                              <div className="text-2xl font-bold text-indigo-700">
                                {formatCurrency(verticalData.totals.net_value)}
                              </div>
                              <div className="text-sm text-red-600">
                                Balance: {formatCurrency(verticalData.totals.current_balance)}
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-400">No loans</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Loans Table */}
                    {isExpanded && hasLoans && (
                      <div className="border-t border-current/20 bg-white">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-100">
                                <th className="text-left p-3 font-semibold text-slate-700">KREDITI</th>
                                <th className="text-right p-3 font-semibold text-slate-700">NV (Total)</th>
                                <th className="text-right p-3 font-semibold text-slate-700">MESEČNI OBROK</th>
                                <th className="text-center p-3 font-semibold text-slate-700">Datum plačila</th>
                                <th className="text-center p-3 font-semibold text-slate-700">ROČNOST KREDITA</th>
                                <th className="text-right p-3 font-semibold text-slate-700">STANJE</th>
                                {isAdmin && <th className="text-center p-3 font-semibold text-slate-700">Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {verticalData.loans.map((loan, idx) => {
                                const isClosed = loan.status === "closed";
                                return (
                                <tr key={loan.id} className={`border-b border-slate-100 ${isClosed ? 'bg-slate-100 opacity-60' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <div className="font-medium text-slate-800">{loan.loan_name}</div>
                                      {isClosed && (
                                        <Badge className="bg-slate-400 text-white text-xs">CLOSED</Badge>
                                      )}
                                    </div>
                                    {loan.bank && (
                                      <div className="text-xs text-slate-500">{loan.bank}</div>
                                    )}
                                  </td>
                                  <td className="p-3 text-right font-bold text-slate-800">
                                    {formatCurrency(loan.net_value)}
                                  </td>
                                  <td className="p-3 text-right text-slate-700">
                                    {formatCurrency(loan.monthly_instalment)}
                                  </td>
                                  <td className="p-3 text-center text-slate-600">
                                    {loan.payment_day ? `${loan.payment_day}.` : "-"}
                                  </td>
                                  <td className="p-3 text-center text-slate-600">
                                    {loan.start_date} - {loan.end_date}
                                  </td>
                                  <td className="p-3 text-right font-bold text-red-600">
                                    {formatCurrency(loan.current_balance)}
                                  </td>
                                  {isAdmin && (
                                    <td className="p-3 text-center">
                                      <div className="flex justify-center gap-1">
                                        {isClosed ? (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleReopenLoan(loan.id);
                                            }}
                                            title="Reopen loan"
                                          >
                                            <RotateCcw className="h-4 w-4 text-green-600" />
                                          </Button>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCloseLoan(loan.id);
                                            }}
                                            title="Close/Archive loan"
                                          >
                                            <Archive className="h-4 w-4 text-amber-600" />
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEdit(loan);
                                          }}
                                          title="Edit loan"
                                        >
                                          <Edit2 className="h-4 w-4 text-blue-600" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(loan.id);
                                          }}
                                          title="Delete permanently"
                                        >
                                          <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                                );
                              })}
                              {/* Vertical Total Row */}
                              <tr className="bg-slate-200 font-bold">
                                <td className="p-3" colSpan={isAdmin ? 1 : 1}>SKUPAJ</td>
                                <td className="p-3 text-right">{formatCurrency(verticalData.totals.net_value)}</td>
                                <td className="p-3 text-right">{formatCurrency(verticalData.totals.monthly_instalment)}</td>
                                <td className="p-3"></td>
                                <td className="p-3"></td>
                                <td className="p-3 text-right text-red-700">{formatCurrency(verticalData.totals.current_balance)}</td>
                                {isAdmin && <td></td>}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {isExpanded && !hasLoans && (
                      <div className="border-t border-current/20 bg-white p-4 text-center text-slate-500">
                        No loans recorded for this company
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* All Loans Tab */}
          <TabsContent value="all-loans">
            <Card>
              <CardHeader>
                <CardTitle>All Active Loans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="text-left p-3 font-semibold text-slate-700">Company</th>
                        <th className="text-left p-3 font-semibold text-slate-700">KREDITI</th>
                        <th className="text-right p-3 font-semibold text-slate-700">NV</th>
                        <th className="text-right p-3 font-semibold text-slate-700">MESEČNI OBROK</th>
                        <th className="text-center p-3 font-semibold text-slate-700">ROČNOST</th>
                        <th className="text-right p-3 font-semibold text-slate-700">STANJE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(dashboard?.by_vertical || {}).flatMap(([verticalId, data]) =>
                        (data.loans || []).map((loan) => (
                          <tr key={loan.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-3">
                              <Badge className={`bg-gradient-to-r ${VERTICAL_COLORS[verticalId]} text-white`}>
                                {getVerticalName(verticalId)}
                              </Badge>
                            </td>
                            <td className="p-3 font-medium">{loan.loan_name}</td>
                            <td className="p-3 text-right">{formatCurrency(loan.net_value)}</td>
                            <td className="p-3 text-right">{formatCurrency(loan.monthly_instalment)}</td>
                            <td className="p-3 text-center text-xs">{loan.start_date} - {loan.end_date}</td>
                            <td className="p-3 text-right font-bold text-red-600">{formatCurrency(loan.current_balance)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-200 font-bold">
                        <td className="p-3" colSpan={2}>GRAND TOTAL</td>
                        <td className="p-3 text-right">{formatCurrency(dashboard?.totals?.total_net_value)}</td>
                        <td className="p-3 text-right">{formatCurrency(dashboard?.totals?.total_monthly_instalment)}</td>
                        <td className="p-3"></td>
                        <td className="p-3 text-right text-red-700">{formatCurrency(dashboard?.totals?.total_current_balance)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {dashboard?.totals?.loan_count === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No loans recorded yet. Click "Add Loan" to get started.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
